<template>
  <nav :class="_class">
    <header
      class="ten-menu-header"
      v-if="this.$scopedSlots.header"
    >
      <!-- @slot 菜单头部 vertical collapsable 时接受 collapsed 作为作用域变量-->
      <slot
        name="header"
        :collapsed="collapsed"
      />
    </header>
    <!-- @slot 菜单列表 -->
    <div class="ten-menu-nav">
      <slot />
    </div>
    <div
      :class="_footerClass"
      v-if="collapsable && mode === 'vertical'"
      @click="tollgeCollapse"
    >
      <ten-icon
        class="ten-menu__footer-carret"
        :icon="footerIcon"
      />
    </div>
  </nav>
</template>

<script>
import TenIcon from '../icon';
import TreeModel from '../tree/tree-model';

const name = 'ten-menu';

export default {
  name,
  components: { TenIcon },
  provide() {
    return {
      menu: this,
      treeNodeParent: this,
      deep: 0,
      paddingDeep: 0,
    };
  },
  props: {
    /**
     * 定义菜单垂直、水平模式
     * @member 'vertical'|'horizontal'
     */
    mode: {
      type: String,
      default: 'vertical',
    },
    /**
     * 菜单样式，ghost 适用于深色背景，ghost 目前只支持 horizontal 模式
     * @member 'default'|'ghost'
     */
    theme: {
      type: String,
      default: 'default',
    },
    /**
     * v-model 选中的 item 的 index
     * 对应 change 事件
     */
    active: [Number, String],
    /**
     * 默认选中的 item 的 index
     */
    defaultActive: [Number, String],
    /**
     * vertical 模式是否允许菜单折叠
     */
    collapsable: Boolean,
    /**
     * vertical 模式菜单折叠，完全折叠，适用于无图标菜单
     */
    collapseAll: Boolean,
    /**
     * vertical 模式菜单头部折叠状态，支持 .sync 修饰符，对应事件 update:collapsed
     */
    collapsed: Boolean,
    /**
     * vertical 模式菜单头部默认折叠状态
     */
    defaultCollapsed: Boolean,
    /**
     * vertical 模式定义展开的子菜单，支持 .sync 修饰符，对应事件 update:expandedIndexes
     */
    expandedIndexes: {
      type: Array,
      default: () => [],
    },
    /**
     * 是否自动展开父级菜单
     */
    expandParent: {
      type: Boolean,
      default: true,
    },
    /**
     * vertical 模式定义默认展开的子菜单
     */
    defaultExpandedIndexes: Array,
    /**
     * vertical 模式展开的子菜单折叠兄弟子菜单
     */
    closeSiblings: Boolean,
    /**
     * 自定义菜单项样式
     */
    itemStyle: Object,
    /**
     * 自定义菜单项选中样式
     */
    activeItemStyle: Object,
    /**
     * 自定义菜单项悬停时样式
     */
    hoverItemStyle: Object,
    /**
     * 自定义子菜单选中样式（子菜单中有选中的菜单项）
     */
    activeSubItemStyle: Object,
    /**
     * 自定义收起状态菜单项选中样式（vertical 模式折叠或 horizontal 模式）
     */
    activeCollapsedItemStyle: Object,
  },
  data() {
    return {
      footerIcon: 'left',
      collapsing: false,
      afterCollapsed: this.collapsed,
      dataExpandedIndexes: [],
    };
  },
  computed: {
    _headerClass() {
      return [
        `${name}__header`,
        `${name}__header--${this.mode}`,
        this.collapsed ? `${name}__header--collapsed` : '',
      ];
    },
    _footerClass() {
      return [
        `${name}__footer`,
        {
          [`${name}__footer--collapsed`]: this.collapsed,
        },
      ];
    },
    _class() {
      return [
        name,
        `${name}--${this.mode}`,
        `${name}--theme-${this.theme}`,
        {
          [`${name}--vertical--collapsable`]: this.collapsable,
          [`${name}--vertical--collapsed`]: this.collapsed,
          [`${name}--vertical--collapse-all`]:
            this.collapsed && this.collapseAll,
          [`${name}--vertical--collapsing`]: this.collapsing,
          [`${name}--vertical--aftercollapsed`]: this.afterCollapsed,
          [`${name}--vertical--collapsed-noicon`]: this.collapsed && this.noIcon,
        },
      ];
    },
  },
  watch: {
    collapsed(v) {
      /**
       * vertical 模式下菜单折叠事件
       * @param { boolean } collapsed
       */
      // this.$emit('update:collapsed', this.collapsed);
      clearTimeout(this.$collapsedT);
      this.collapsing = true;
      this.$collapsedT = setTimeout(() => {
        this.collapsing = false;
        this.afterCollapsed = this.collapsed;
      }, 200);
      if (!v) {
        this.afterCollapsed = false;
      }
    },
    active: {
      handler(v) {
        this.applySelected([v]);
      },
      immediate: true,
    },
    defaultExpandedIndexes: {
      handler(v) {
        if (
          'defaultExpandedIndexes' in this.$vnode.componentOptions.propsData
          && !('expandedIndexes' in this.$vnode.componentOptions.propsData)
        ) {
          this.dataExpandedIndexes = v;
        }
      },
      immediate: true,
    },
    expandedIndexes: {
      handler(v) {
        if ('expandedIndexes' in this.$vnode.componentOptions.propsData) {
          this.dataExpandedIndexes = v;
        }
      },
      immediate: true,
    },
    dataExpandedIndexes(v) {
      this.applyExpanded(v);
    },
  },
  beforeCreate() {
    this.$collapsedT = 0;
    this.nodeModel = new TreeModel();
  },
  created() {
    this.nodeModel.setConfig({
      expandParent: this.expandParent,
      closeSiblings: this.closeSiblings,
    });
  },
  mounted() {
    if (
      !('expandedIndexes' in this.$vnode.componentOptions.propsData)
      && !('defaultExpandedIndexes' in this.$vnode.componentOptions.propsData)
    ) {
      const expandedIndexes = typeof this.active !== 'undefined' ? [this.active] : [];
      this.applyExpanded(expandedIndexes);
    } else {
      this.applyExpanded(this.dataExpandedIndexes);
    }
  },
  methods: {
    applySelected(selectedKeys) {
      this.nodeModel.setSelected(selectedKeys);
      this.nodeModel.applySelected();
    },
    setActive(item) {
      return this.$emit('change', item.index);
    },
    applyExpanded(expandedKeys) {
      this.nodeModel.setExpanded(expandedKeys);
      this.nodeModel.applyExpanded();
    },
    onNodeExpandChange(key, expanded) {
      this.nodeModel.handleExpandedChange(key, expanded);
      const expandedKeys = Array.prototype.concat.apply(
        [],
        this.nodeModel.children.map(child => child.getExpanded())
      );
      this.$emit('update:expandedIndexes', expandedKeys);
      if (!('expandedIndexes' in this.$vnode.componentOptions.propsData)) {
        // uncontrolled
        this.dataExpandedIndexes = expandedKeys;
      }
    },
    setCollapse(collapsed) {
      this.$emit('update:collapsed', collapsed);
    },
    tollgeCollapse() {
      this.setCollapse(!this.collapsed);
    },
  },
};
</script>
